import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useState } from "react";

import { DupDefaultCoverage } from "../../../components/dup/calculation/DupDefaultCoverage";
import { Insureds } from "../../../components/dup/calculation/Insureds";
import DupSummary from "../../../components/dup/DupSummary";
import { Step } from "../../../components/stepIndicator/Step";
import { StepIndicator } from "../../../components/stepIndicator/StepIndicator";
import { scrollToFirstValidationMessage } from "../../../components/validationMessage/scrollToFirstValidationMessage";
import { ValidationMessage } from "../../../components/validationMessage/ValidationMessage";
import { useDupFlowContext } from "../../../hooks/useDupContext";
import useIsClient from "../../../lib/useIsClient";
import { isInsuredComplete } from "../../../providers/dupFlow/DupFlow.helpers";
import { FlowLayout } from "../../FlowLayout";

interface Props {
  fields: Record<string, Field<string>>;
}

export const DupFlowStepOne = (props: Props) => {
  const { isClient } = useIsClient();
  const [loading, setLoading] = useState(false);
  const { data, insuredAmounts, setters, premium, isLoadingPremium, modelState, setModelState, goToStepTwo } =
    useDupFlowContext();

  const hasCompletedInsureds = data.insuredPersons.filter(isInsuredComplete).length > 0;

  const goToStep2 = async () => {
    try {
      setLoading(true);
      await goToStepTwo(props.fields.Step2Path.value, props.fields.PolicyCostValue.value);
    } catch {
      setLoading(false);
      scrollToFirstValidationMessage();
    }
  };

  return (
    <>
      <StepIndicator activeStepIndex={0}>
        <Step label={props.fields.step1Name.value} labelMobile={props.fields.step1MobileName.value} />
        <Step label={props.fields.step2Name.value} labelMobile={props.fields.step2MobileName.value} />
        <Step label={props.fields.step3Name.value} labelMobile={props.fields.step3MobileName.value} />
        <Step label={props.fields.step4Name.value} labelMobile={props.fields.step4MobileName.value} />
      </StepIndicator>
      <FlowLayout
        Sidebar={
          <DupSummary
            {...props}
            submitButtonProps={{
              id: "createInvoiceButtonId",
              children: props.fields.CreateInvoiceSubmitButtonText.value,
              disabled: premium === null || isLoadingPremium,
              loading,
              onClick: goToStep2,
            }}
          />
        }
        {...props}
      >
        <ValidationMessage id="global" modelState={modelState} property="Global" />
        <Insureds
          fields={props.fields}
          insuredPersons={data.insuredPersons}
          defaultCoverage={data.defaultCoverage}
          onUpdateInsured={setters.updateInsuredData}
          onUpdateInsuredDateOfBirth={setters.updateInsuredDateOfBirth}
          onUpdateChildAsAdultPremium={setters.updateChildAsAdultPremium}
          onAddInsuredPerson={setters.addInsuredPerson}
          onRemovePerson={setters.removePerson}
          modelState={modelState}
          setModelState={setModelState}
        />
        {isClient && (
          <DupDefaultCoverage
            hasCompletedInsureds={hasCompletedInsureds}
            fields={props.fields}
            insuredPersons={data.insuredPersons}
            defaultCoverage={data.defaultCoverage}
            insuredAmounts={insuredAmounts}
            onUpdateInsuranceType={setters.updateInsuranceType}
            onUpdateInsuredAmount={setters.updateInsuredAmount}
            onUpdateHasInsuredAmount={setters.updateHasInsuredAmount}
            onUpdatePaymentPeriod={setters.updatePaymentPeriod}
            onUpdateEffectiveDate={setters.updateEffectiveDate}
            modelState={modelState}
            setModelState={setModelState}
          />
        )}
      </FlowLayout>
    </>
  );
};
